import * as Sentry from '@sentry/react';
import { onCLS, onINP, onLCP, onFCP, onTTFB } from 'web-vitals';

/**
 * Core Web Vitals tracking thresholds (Google's recommendations)
 */
const VITALS_THRESHOLDS = {
  LCP: 2500, // Large Contentful Paint: <2.5s is good
  INP: 200,  // Interaction to Next Paint: <200ms is good (replaces FID)
  CLS: 0.1,  // Cumulative Layout Shift: <0.1 is good
  FCP: 1800, // First Contentful Paint: <1.8s is good
  TTFB: 800, // Time to First Byte: <800ms is good
};

/**
 * Frontend monitoring configuration for error tracking and performance monitoring
 */
export function initializeMonitoring() {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE || 'development',
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    tracesSampleRate: import.meta.env.MODE === 'production' ? 0.1 : 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    beforeSend(event) {
      // Filter out development noise
      if (import.meta.env.MODE === 'development') {
        // Filter out common development errors
        if (event.exception?.values?.[0]?.value?.includes('ResizeObserver')) {
          return null;
        }
        if (event.exception?.values?.[0]?.value?.includes('Non-Error promise rejection')) {
          return null;
        }
      }
      return event;
    }
  });

  // Initialize Web Vitals tracking
  initializeWebVitals();
}

/**
 * Initialize Core Web Vitals tracking and send data to Sentry
 */
function initializeWebVitals() {
  // Track Largest Contentful Paint (LCP)
  onLCP((metric) => {
    const rating = metric.value <= VITALS_THRESHOLDS.LCP ? 'good' : metric.value <= 4000 ? 'needs-improvement' : 'poor';
    
    Sentry.setMeasurement('lcp', metric.value, 'millisecond');
    Sentry.addBreadcrumb({
      category: 'web-vitals',
      message: `LCP: ${metric.value}ms (${rating})`,
      level: rating === 'good' ? 'info' : rating === 'needs-improvement' ? 'warning' : 'error',
      data: {
        value: metric.value,
        rating,
        threshold: VITALS_THRESHOLDS.LCP,
        id: metric.id,
        element: metric.entries[0]?.element || null,
      },
    });
  });

  // Track Interaction to Next Paint (INP) - replaces FID
  onINP((metric) => {
    const rating = metric.value <= VITALS_THRESHOLDS.INP ? 'good' : metric.value <= 500 ? 'needs-improvement' : 'poor';
    
    Sentry.setMeasurement('inp', metric.value, 'millisecond');
    Sentry.addBreadcrumb({
      category: 'web-vitals',
      message: `INP: ${metric.value}ms (${rating})`,
      level: rating === 'good' ? 'info' : rating === 'needs-improvement' ? 'warning' : 'error',
      data: {
        value: metric.value,
        rating,
        threshold: VITALS_THRESHOLDS.INP,
        id: metric.id,
      },
    });
  });

  // Track Cumulative Layout Shift (CLS)
  onCLS((metric) => {
    const rating = metric.value <= VITALS_THRESHOLDS.CLS ? 'good' : metric.value <= 0.25 ? 'needs-improvement' : 'poor';
    
    Sentry.setMeasurement('cls', metric.value, 'seconds');
    Sentry.addBreadcrumb({
      category: 'web-vitals',
      message: `CLS: ${metric.value} (${rating})`,
      level: rating === 'good' ? 'info' : rating === 'needs-improvement' ? 'warning' : 'error',
      data: {
        value: metric.value,
        rating,
        threshold: VITALS_THRESHOLDS.CLS,
        id: metric.id,
      },
    });
  });

  // Track First Contentful Paint (FCP)
  onFCP((metric) => {
    const rating = metric.value <= VITALS_THRESHOLDS.FCP ? 'good' : metric.value <= 3000 ? 'needs-improvement' : 'poor';
    
    Sentry.setMeasurement('fcp', metric.value, 'millisecond');
    Sentry.addBreadcrumb({
      category: 'web-vitals',
      message: `FCP: ${metric.value}ms (${rating})`,
      level: 'info',
      data: {
        value: metric.value,
        rating,
        threshold: VITALS_THRESHOLDS.FCP,
        id: metric.id,
      },
    });
  });

  // Track Time to First Byte (TTFB)
  onTTFB((metric) => {
    const rating = metric.value <= VITALS_THRESHOLDS.TTFB ? 'good' : metric.value <= 1800 ? 'needs-improvement' : 'poor';
    
    Sentry.setMeasurement('ttfb', metric.value, 'millisecond');
    Sentry.addBreadcrumb({
      category: 'web-vitals',
      message: `TTFB: ${metric.value}ms (${rating})`,
      level: 'info',
      data: {
        value: metric.value,
        rating,
        threshold: VITALS_THRESHOLDS.TTFB,
        id: metric.id,
      },
    });
  });
}

/**
 * Capture errors with additional context
 */
export function captureError(error: Error | string, context?: Record<string, any>) {
  if (context) {
    Sentry.setContext('error_context', context);
  }
  if (typeof error === 'string') {
    Sentry.captureMessage(error, 'error');
  } else {
    Sentry.captureException(error);
  }
}

/**
 * Set user context for error tracking
 */
export function setUser(userId: string, email?: string, role?: string) {
  Sentry.setUser({
    id: userId,
    email,
    role
  });
}

/**
 * Performance monitoring wrapper
 */
export function withPerformance<T extends (...args: any[]) => any>(
  fn: T,
  operationName: string
): T {
  return ((...args: any[]) => {
    return Sentry.startSpan({ name: operationName }, () => {
      return fn(...args);
    });
  }) as T;
}

/**
 * React error boundary component
 */
export const ErrorBoundary = Sentry.withErrorBoundary;